import { Modal } from 'antd';
import { Github } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import iconSvg from '@/assets/icon.svg';
import { authStore } from '@/stores';
import { AuthType } from '@/types';

interface LoginDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CODE_LENGTH = 6;

function createEmptyCodes(): string[] {
  return Array.from({ length: CODE_LENGTH }, () => '');
}

export const LoginDialog = observer(
  ({ open, onOpenChange }: LoginDialogProps) => {
    const [email, setEmail] = useState('');
    const [emailStep, setEmailStep] = useState<'email' | 'verify'>('email');
    const [codes, setCodes] = useState<string[]>(createEmptyCodes());
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const isBusy =
      authStore.isLoggingIn || authStore.isSendingCode || authStore.isVerifying;
    const verificationEmail = authStore.verificationEmail || email.trim();
    const joinedCode = codes.join('');

    // biome-ignore lint/correctness/useExhaustiveDependencies: authStore.isAuthenticated is a MobX observable read that must stay in the effect dependencies.
    useEffect(() => {
      if (authStore.isAuthenticated && open) {
        onOpenChange?.(false);
      }
    }, [authStore.isAuthenticated, open, onOpenChange]);

    useEffect(() => {
      if (open) {
        return;
      }

      setEmail('');
      setEmailStep('email');
      setCodes(createEmptyCodes());
    }, [open]);

    useEffect(() => {
      if (open && emailStep === 'verify') {
        inputRefs.current[0]?.focus();
      }
    }, [emailStep, open]);

    const handleOAuthLogin = (type: AuthType) => {
      authStore.loginWithOAuth(type);
    };

    const handleSendEmailCode = async () => {
      const trimmedEmail = email.trim();
      const success = await authStore.sendEmailAuthCode(trimmedEmail);
      if (!success) {
        return;
      }

      setEmail(authStore.verificationEmail || trimmedEmail);
      setEmailStep('verify');
      setCodes(createEmptyCodes());
    };

    const handleEmailLogin = async (codeOverride?: string) => {
      const success = await authStore.loginWithEmail(
        verificationEmail,
        codeOverride ?? joinedCode,
      );
      if (!success) {
        setCodes(createEmptyCodes());
        window.setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 0);
        return;
      }

      setCodes(createEmptyCodes());
    };

    const handleCodeChange = (index: number, value: string) => {
      const nextValue = value.replace(/[^0-9]/g, '').slice(-1);
      const nextCodes = [...codes];
      nextCodes[index] = nextValue;
      setCodes(nextCodes);

      if (nextValue && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      if (nextCodes.every(Boolean)) {
        void handleEmailLogin(nextCodes.join(''));
      }
    };

    const handleCodeKeyDown = (
      index: number,
      event: KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === 'Backspace' && !codes[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const handleCodePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedCode = event.clipboardData
        .getData('text')
        .replace(/[^0-9]/g, '')
        .slice(0, CODE_LENGTH);

      const nextCodes = createEmptyCodes();
      pastedCode.split('').forEach((digit, index) => {
        nextCodes[index] = digit;
      });
      setCodes(nextCodes);

      const nextIndex = Math.min(pastedCode.length, CODE_LENGTH - 1);
      inputRefs.current[nextIndex]?.focus();

      if (pastedCode.length === CODE_LENGTH) {
        void handleEmailLogin(pastedCode);
      }
    };

    const handleResendCode = async () => {
      const success = await authStore.resendEmailAuthCode();
      if (!success) {
        return;
      }

      setCodes(createEmptyCodes());
      window.setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    };

    const handleBackToEmail = () => {
      setEmailStep('email');
      setCodes(createEmptyCodes());
    };

    return (
      <Modal
        open={open}
        onCancel={() => {}}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={440}
        centered
        style={{
          borderRadius: '12px',
          backgroundColor: '#ffffff',
        }}
        styles={{
          body: {
            padding: '40px',
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
          header: {
            padding: 0,
            border: 'none',
            marginBottom: 0,
          },
        }}
        className="login-modal"
      >
        <div className="w-full relative">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center gap-2 mb-3">
              <img src={iconSvg} alt="Aneway Logo" className="w-11 h-11" />
              <span className="text-2xl font-bold text-black">Aneway</span>
            </div>
            {emailStep === 'verify' ? (
              <>
                <p className="text-sm text-gray-600 text-center">
                  请输入发送到以下邮箱的 6 位验证码
                </p>
                <p className="text-sm font-medium text-black break-all mt-1">
                  {verificationEmail}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                Sign in to continue your creation
              </p>
            )}
          </div>

          {emailStep === 'email' ? (
            <>
              <div className="space-y-3 mb-6">
                <button
                  type="button"
                  onClick={() => handleOAuthLogin(AuthType.Google)}
                  disabled={isBusy}
                  className="w-full h-12 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center text-base font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed border-0 shadow-none"
                  style={{
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <title>Google</title>
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {authStore.isLoggingIn ? '登录中...' : 'Continue with Google'}
                </button>

                <button
                  type="button"
                  onClick={() => handleOAuthLogin(AuthType.GitHub)}
                  disabled={isBusy}
                  className="w-full h-12 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center text-base font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed border-0 shadow-none"
                  style={{
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Github className="w-5 h-5 mr-3 text-black" />
                  {authStore.isLoggingIn ? '登录中...' : 'Continue with Github'}
                </button>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs text-gray-500">
                  Or continue with email
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <div className="space-y-3 mb-6">
                <input
                  type="email"
                  className="w-full h-12 rounded-lg border border-gray-200 bg-white px-4 text-sm text-black outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isBusy) {
                      void handleSendEmailCode();
                    }
                  }}
                  autoComplete="email"
                  disabled={isBusy}
                />

                <button
                  type="button"
                  onClick={() => {
                    void handleSendEmailCode();
                  }}
                  disabled={isBusy || !email.trim()}
                  className="w-full h-12 rounded-lg bg-black text-base font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {authStore.isSendingCode
                    ? 'Sending code...'
                    : 'Send verification code'}
                </button>
              </div>
            </>
          ) : (
            <div className="mb-6">
              <div className="flex justify-center gap-2 mb-4">
                {codes.map((code, index) => (
                  <input
                    key={`login-code-${index + 1}`}
                    ref={(element) => {
                      inputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={code}
                    onChange={(event) =>
                      handleCodeChange(index, event.target.value)
                    }
                    onKeyDown={(event) => handleCodeKeyDown(index, event)}
                    onPaste={handleCodePaste}
                    disabled={isBusy}
                    className="h-12 w-11 rounded-lg border border-gray-200 bg-white text-center text-lg font-semibold text-black outline-none transition-colors focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  void handleEmailLogin();
                }}
                disabled={isBusy || joinedCode.length !== CODE_LENGTH}
                className="w-full h-12 rounded-lg bg-black text-base font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {authStore.isVerifying ? 'Verifying...' : 'Continue with Email'}
              </button>

              <div className="mt-4 flex items-center justify-between gap-4 text-sm">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  disabled={isBusy}
                  className="text-gray-500 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleResendCode();
                  }}
                  disabled={isBusy}
                  className="font-medium text-gray-700 transition-colors hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {authStore.isSendingCode ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          <div className="mt-2 text-center">
            <p className="text-xs text-gray-500 leading-relaxed">
              By continuing, you acknowledge that you understand and agree to
              the{' '}
              <button
                type="button"
                className="font-bold underline text-gray-500 hover:text-gray-700"
                onClick={() => {
                  // TODO: Navigate to terms of service
                }}
              >
                Terms of service
              </button>{' '}
              and{' '}
              <button
                type="button"
                className="font-bold underline text-gray-500 hover:text-gray-700"
                onClick={() => {
                  // TODO: Navigate to privacy policy
                }}
              >
                Privacy Policy
              </button>
              .
            </p>
          </div>
        </div>
      </Modal>
    );
  },
);
